import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from '../../components/BreadCrumbs';
import {
  RootState,
  fetchActiveAlertsRequest,
  fetchAlertContactPreferencesRequest,
  updateAlertPreferencesRequest,
  clearErrors,
  environment,
  webFormatCurrency,
} from '@rb-serve-app/core';
import Spinner from '../../components/Spinner';
import CardDropdown from '../../components/CardDropdown';
import ManageAlertsModals from '../../components/ManageAlertsModals';
import AlertNotificationSwitch from '../../components/AlertNotificationSwitch';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import Modal from 'react-modal';
import ChangeAlertPreferences from '../../components/ChangeAlertPreferences';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useRecaptchaToken } from '@rb-serve-app/core';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { logBrazeCustomEvent } from '../../config/braze';

interface AlertDetail {
  name: string;
  selectedValue: string | string[];
  availableValues?: string[];
}

interface Alert {
  alert: string;
  channels: {
    EMAIL?: string;
    SMS?: string;
  };
  alertDetails: AlertDetail[];
  activeFlag: string;
}

interface AlertCategory {
  categoryName: string;
  details: Alert[];
  activeAlertCount: number;
}

interface SwitchStates {
  [alertType: string]: { email: boolean; sms: boolean };
}

interface AvailableChannels {
  [alertType: string]: { hasEmail: boolean; hasSms: boolean };
}

interface InputValues {
  [alertType: string]: any;
}

interface InputErrors {
  [alertType: string]: boolean;
}

interface PendingCreditAlertToggle {
  alertId: string;
  type: string;
}

interface ManageAlertsRouteProps {
  route?: {
    selectedCard?: {
      customerId: string | number;
    };
  };
}

const ManageAlerts: React.FC<ManageAlertsRouteProps> = ({ route }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    logBrazeCustomEvent('viewed_manage_alerts');
  }, []);

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { entries, assets } = contentfulData;
  const { rb_manage_alerts, rb_navigation } = entries;
  const alertTypeWeb = rb_manage_alerts?.alertTypeWeb;
  const alertTypeMapping =
    rb_manage_alerts?.webAlertsHeader?.rbManageAlertsWebMapping
      ?.alertTypeMapping;
  const countryCodeMapping =
    rb_manage_alerts?.webAlertsHeader?.rbManageAlertsWebMapping
      ?.countryCodeMapping;
  const stateProvinceCodeMapping =
    rb_manage_alerts?.webAlertsHeader?.rbManageAlertsWebMapping
      .stateProvinceCodeMapping;

  const { accountDetails } = useSelector((state: RootState) => state.user);

  const {
    alerts,
    alertsLoading,
    error,
    alertContactPrefs,
    contactPrefs,
    updateLoading,
  } = useSelector((state: RootState) => state.manageAlerts);

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [inputValues, setInputValues] = useState<InputValues>({});
  const [isFocused, setIsFocused] = useState<Record<string, boolean>>({});
  const [switchStates, setSwitchStates] = useState<SwitchStates>({});
  const [initialSwitchStates, setInitialSwitchStates] = useState<SwitchStates>(
    {}
  );
  const [initialInputValues, setInitialInputValues] = useState<InputValues>({});
  const [availableChannels, setAvailableChannels] = useState<AvailableChannels>(
    {}
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [inputErrors, setInputErrors] = useState<InputErrors>({});
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [smsDisabled, setSmsDisabled] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [verificationPopupMessage, setVerificationPopupMessage] = useState('');
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [popupType, setPopupType] = useState<'sms' | 'email'>('sms');
  const [pendingCreditAlertToggle, setPendingCreditAlertToggle] =
    useState<PendingCreditAlertToggle | null>(null);
  const [showCreditAlertConfirmation, setShowCreditAlertConfirmation] =
    useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [availableCards, setAvailableCards] = useState<any[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [unsavedChangesContext, setUnsavedChangesContext] = useState<
    'navigation' | 'form'
  >('form');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [inputErrorMessages, setInputErrorMessages] = useState<
    Record<string, string>
  >({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [showDisableAllConfirmation, setShowDisableAllConfirmation] =
    useState(false);
  const [shouldAutoUpdate, setShouldAutoUpdate] = useState(false);
  const previousCustomerIdRef = useRef<string | number | null>(null);
  const isMounted = useRef(false);
  const wasUpdating = useRef(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');

  // Helper functions for alert type mapping
  const getCategoryDisplayName = (categoryKey: string) => {
    return alertTypeMapping?.[categoryKey]?.categoryDisplayName || categoryKey;
  };

  // Add function to check if alert exists in contentful mapping
  const isAlertInMapping = (alertType: string) => {
    if (!alertTypeMapping) {
      return false;
    }

    // Check if alert exists in any category
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        return true;
      }
    }
    return false;
  };

  // Add function to check if alert should be shown on web
  const shouldShowAlertOnWeb = (alertType: string) => {
    if (!alertTypeMapping) {
      return true; // Default to showing if mapping doesn't exist
    }

    // Find the alert in any category and check showAlertWeb flag
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        return category.alerts[alertType].showAlertWeb !== false; // Default to true if not specified
      }
    }
    return true; // Default to showing if alert not found in mapping
  };

  // Add function to check if entire category should be hidden on web
  const shouldHideEntireCategoryOnWeb = (categoryKey: string) => {
    if (!alertTypeMapping || !alertTypeMapping[categoryKey]) {
      return false; // Default to showing if mapping doesn't exist
    }
    return alertTypeMapping[categoryKey].hideEntireCategoryWeb === true;
  };

  // Add function to check if category display name should be shown on web
  const shouldShowCategoryDisplayNameOnWeb = (categoryKey: string) => {
    if (!alertTypeMapping || !alertTypeMapping[categoryKey]) {
      return true; // Default to showing if mapping doesn't exist
    }
    return alertTypeMapping[categoryKey].showCategoryDisplayNameWeb !== false; // Default to true if not specified
  };

  // Add function to get category key from category name
  const getCategoryKeyFromName = (categoryName: string) => {
    // Add null/undefined check
    if (!categoryName) {
      return '';
    }

    if (!alertTypeMapping) {
      return categoryName;
    }

    // Normalize the category name for comparison (handle different cases like "Payment Alerts" vs "PAYMENT_ALERTS")
    const normalizedCategoryName = categoryName
      .toUpperCase()
      .replace(/\s+/g, '_');

    // Find the category key that matches this category name
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      const normalizedKey = categoryKey.toUpperCase().replace(/\s+/g, '_');
      const normalizedDisplayName = category.categoryDisplayName
        ?.toUpperCase()
        .replace(/\s+/g, '_');

      // Try multiple matching strategies
      if (
        normalizedDisplayName === normalizedCategoryName ||
        normalizedKey === normalizedCategoryName ||
        category.categoryDisplayName === categoryName ||
        categoryKey === categoryName
      ) {
        return categoryKey;
      }
    }
    return categoryName; // Fallback to the category name itself
  };

  const getAlertDisplayName = (alertType: string) => {
    if (!alertTypeMapping) {
      return alertType
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
    }

    // Find the alert in any category
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        return category.alerts[alertType].displayName;
      }
    }

    // Fallback to formatted alert type
    return alertType
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getAlertInputLabel = (alertType: string) => {
    if (!alertTypeMapping) {
      return null;
    }

    // Find the alert in any category
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (
        category.alerts &&
        category.alerts[alertType] &&
        category.alerts[alertType].inputLabel
      ) {
        return category.alerts[alertType].inputLabel;
      }
    }

    return null;
  };

  const getCountryDisplayName = (countryCode: string) => {
    return countryCodeMapping?.[countryCode] || countryCode;
  };

  const getStateProvinceDisplayName = (stateProvinceCode: string) => {
    return stateProvinceCodeMapping?.[stateProvinceCode] || stateProvinceCode;
  };

  // Helper functions for dropdown options mapping
  const getCountryOptionsForDropdown = (availableCountryCodes: string[]) => {
    if (!countryCodeMapping) return availableCountryCodes;
    return availableCountryCodes.map((code) => getCountryDisplayName(code));
  };

  const getStateProvinceOptionsForDropdown = (
    availableStateCodes: string[]
  ) => {
    if (!stateProvinceCodeMapping) return availableStateCodes;
    return availableStateCodes.map((code) => getStateProvinceDisplayName(code));
  };

  const getCountryCodesFromDisplayNames = (displayNames: string[]) => {
    if (!countryCodeMapping) return displayNames;
    const reverseMapping = Object.fromEntries(
      Object.entries(countryCodeMapping).map(([code, name]) => [name, code])
    );
    return displayNames.map((name) => reverseMapping[name] || name);
  };

  const getStateProvinceCodesFromDisplayNames = (displayNames: string[]) => {
    if (!stateProvinceCodeMapping) return displayNames;
    const reverseMapping = Object.fromEntries(
      Object.entries(stateProvinceCodeMapping).map(([code, name]) => [
        name,
        code,
      ])
    );
    return displayNames.map((name) => reverseMapping[name] || name);
  };

  // Helper functions to get min/max values from contentful
  // const getAlertMinValue = (alertType: string): number => {
  //   if (!alertTypeMapping) {
  //     return alertType === 'PAYMENT_DUE' ? 1 : 0; // Default fallback
  //   }

  //   // Find the alert in any category
  //   for (const categoryKey of Object.keys(alertTypeMapping)) {
  //     const category = alertTypeMapping[categoryKey];
  //     if (category.alerts && category.alerts[alertType]) {
  //       return (
  //         category.alerts[alertType].minValue ||
  //         (alertType === 'PAYMENT_DUE' ? 1 : 0)
  //       );
  //     }
  //   }

  //   return alertType === 'PAYMENT_DUE' ? 1 : 0; // Default fallback
  // };

   const getAlertMinValue = (alertType: string): number => {
    if (!alertTypeMapping) {
      if (alertType === 'PAYMENT_DUE') return 1;
      if (alertType === 'AVAIL_CREDIT_REMAINING') return 1.0;
      if (alertType === 'PURCHASE_AMOUNT') return 1.0;
      if (alertType === 'BALANCE_ABOVE_THRESHOLD') return 0.01;
      return 0;
    }

    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        const configuredMinValue = category.alerts[alertType].minValue;
        if (configuredMinValue !== undefined) return configuredMinValue;

        // Default minimum values if not configured
        if (alertType === 'PAYMENT_DUE') return 1;
        if (alertType === 'AVAIL_CREDIT_REMAINING') return 1.0;
        if (alertType === 'PURCHASE_AMOUNT') return 1.0;
        if (alertType === 'BALANCE_ABOVE_THRESHOLD') return 0.01;
        return 0;
      }
    }

    if (alertType === 'PAYMENT_DUE') return 1;
    if (alertType === 'AVAIL_CREDIT_REMAINING') return 1.0;
    if (alertType === 'PURCHASE_AMOUNT') return 1.0;
    if (alertType === 'BALANCE_ABOVE_THRESHOLD') return 0.01;
    return 0;
  };

  const getAlertMaxValue = (alertType: string): number => {
    if (!alertTypeMapping) {
      return alertType === 'PAYMENT_DUE' ? 20 : Number.MAX_SAFE_INTEGER; // Default fallback
    }

    // Find the alert in any category
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        return (
          category.alerts[alertType].maxValue ||
          (alertType === 'PAYMENT_DUE' ? 20 : Number.MAX_SAFE_INTEGER)
        );
      }
    }

    return alertType === 'PAYMENT_DUE' ? 20 : Number.MAX_SAFE_INTEGER; // Default fallback
  };

  const getAlertInlineErrorMax = (alertType: string): string | null => {
    if (!alertTypeMapping) {
      return null;
    }

    // Find the alert in any category
    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts && category.alerts[alertType]) {
        return category.alerts[alertType].inlineErrorMax || null;
      }
    }

    return null;
  };

  const getAlertMinInlineError = (alertType: string): string | null => {
    console.log("message1", alertType, alertTypeMapping);
    if (!alertTypeMapping) {
      return null;
    }

    for (const categoryKey of Object.keys(alertTypeMapping)) {
      const category = alertTypeMapping[categoryKey];
      if (category.alerts?.[alertType]) {
        console.log("message2",category ,category.alerts[alertType],category.alerts[alertType].minInlineError);
        return category.alerts[alertType].minInlineError || null;
      }
    }

    return null;
  };

  // Helper function to format currency values based on locale
  const formatCurrencyForDisplay = (value: string | number): string => {
    if (!value || value === '') return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    const lang = selectedLanguage === 'fr' ? 'fr' : 'en';
    return webFormatCurrency(numValue, lang);
  };

  // Helper function to get placeholder text based on locale
  const getCurrencyPlaceholder = (): string => {
    const lang = selectedLanguage === 'fr' ? 'fr' : 'en';
    return lang === 'fr' ? '0,00 $' : '$0.00';
  };

  const getSelectedCountryDisplayNames = (selectedCodes: string[]) => {
    if (!countryCodeMapping) return selectedCodes;
    return selectedCodes.map((code) => getCountryDisplayName(code));
  };

  const getSelectedStateProvinceDisplayNames = (selectedCodes: string[]) => {
    if (!stateProvinceCodeMapping) return selectedCodes;
    return selectedCodes.map((code) => getStateProvinceDisplayName(code));
  };

  useEffect(() => {
    if (accountDetails) {
      const accountId = accountDetails.accountId;
      const customerId = accountDetails.customer?.customerId;

      if (accountId && customerId) {
        dispatch(
          fetchActiveAlertsRequest({
            accountId,
            customerId,
          })
        );
        dispatch(
          fetchAlertContactPreferencesRequest({
            accountId,
            customerId,
          })
        );
      }
    }
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, [dispatch, accountDetails]);

  useEffect(() => {
    if (
      selectedCard &&
      isMounted.current &&
      previousCustomerIdRef.current !== selectedCard?.customerId
    ) {
      const accountId = accountDetails?.accountId;
      const customerId = selectedCard?.customerId;
      if (accountId && customerId) {
        dispatch(
          fetchActiveAlertsRequest({
            accountId,
            customerId,
          })
        );
        dispatch(
          fetchAlertContactPreferencesRequest({
            accountId,
            customerId,
          })
        );
        previousCustomerIdRef.current = customerId;
      }
    }
  }, [dispatch, accountDetails, selectedCard, availableCards]);

  useEffect(() => {
    if (alerts && alerts.length > 0) {
      const newSwitchStates: SwitchStates = {};
      const newInputValues: InputValues = {};
      const newAvailableChannels: AvailableChannels = {};

      // Process alerts by category and auto-expand categories without display names
      const categoriesToAutoExpand: string[] = [];

      alerts.forEach((category: AlertCategory) => {
        const categoryKey = getCategoryKeyFromName(category.categoryName);

        // Check if category should be auto-expanded (when display name is hidden)
        if (
          !shouldShowCategoryDisplayNameOnWeb(categoryKey) &&
          !shouldHideEntireCategoryOnWeb(categoryKey)
        ) {
          categoriesToAutoExpand.push(category.categoryName);
        }

        category.details?.forEach((alert: Alert) => {
          const alertType = alert.alert;

          // Only process alerts that exist in the contentful alertTypeMapping and should be shown on web
          if (
            !isAlertInMapping(alertType) ||
            !shouldShowAlertOnWeb(alertType)
          ) {
            return; // Skip this alert if it's not in the mapping or not shown on web
          }

          // Track which channels are available for this alert
          // A channel is considered available only if it exists as a property in the channels object
          const hasEmail = alert.channels && 'EMAIL' in alert.channels;
          const hasSms = alert.channels && 'SMS' in alert.channels;

          newAvailableChannels[alertType] = {
            hasEmail,
            hasSms,
          };

          // Process switch states - only for available channels
          const emailEnabled = hasEmail && alert.channels?.EMAIL === 'true';
          const smsEnabled = hasSms && alert.channels?.SMS === 'true';
          newSwitchStates[alertType] = {
            email: emailEnabled,
            sms: smsEnabled,
          };

          // Process input values for alerts that have them
          if (alert.alertDetails && alert.alertDetails.length > 0) {
            alert.alertDetails.forEach((detail: AlertDetail) => {
              if (detail.name === 'thresholdAmount') {
                const amount = parseFloat(detail.selectedValue as string);
                newInputValues[alertType] = !isNaN(amount)
                  ? amount.toFixed(2)
                  : '0.00';
              } else if (detail.name === 'daysBeforeDueDate') {
                newInputValues[alertType] = detail.selectedValue;
              } else if (detail.name === 'creditAmount') {
                const amount = parseFloat(detail.selectedValue as string);
                newInputValues[alertType] = !isNaN(amount)
                  ? amount.toFixed(2)
                  : '0.00';
              } else if (detail.name === 'listOfCountries') {
                newInputValues[alertType] = Array.isArray(detail.selectedValue)
                  ? detail.selectedValue
                  : [detail.selectedValue];
              } else if (detail.name === 'listOfStatesProvinces') {
                newInputValues[alertType] = Array.isArray(detail.selectedValue)
                  ? detail.selectedValue
                  : [detail.selectedValue];
              }
            });
          }
        });
      });

      setSwitchStates(newSwitchStates);
      setInputValues(newInputValues);
      setAvailableChannels(newAvailableChannels);
      setInitialSwitchStates(JSON.parse(JSON.stringify(newSwitchStates)));
      setInitialInputValues(JSON.parse(JSON.stringify(newInputValues)));
      setHasChanges(false);

      // Set initial validation errors
      const initialErrors: InputErrors = {};
      Object.keys(newSwitchStates).forEach((alertType) => {
        const switchState = newSwitchStates[alertType];
        const alertsWithInput = [
          'PURCHASE_AMOUNT',
          'PAYMENT_DUE',
          'AVAIL_CREDIT_REMAINING',
          'BALANCE_ABOVE_THRESHOLD',
        ];

        if (alertsWithInput.includes(alertType)) {
          const hasEnabledChannels =
            (newAvailableChannels[alertType]?.hasEmail && switchState.email) ||
            (newAvailableChannels[alertType]?.hasSms && switchState.sms);

          if (alertType === 'PAYMENT_DUE') {
            const minValue = getAlertMinValue(alertType);
            const maxValue = getAlertMaxValue(alertType);
            initialErrors[alertType] =
              hasEnabledChannels &&
              (!newInputValues[alertType] ||
                newInputValues[alertType].toString().trim() === '' ||
                newInputValues[alertType] < minValue ||
                newInputValues[alertType] > maxValue);
          } else if (alertType === 'PURCHASE_AMOUNT') {
            const minValue = getAlertMinValue(alertType);
            initialErrors[alertType] =
              hasEnabledChannels &&
              (!newInputValues[alertType] ||
                newInputValues[alertType].toString().trim() === '' ||
                Number.parseFloat(newInputValues[alertType]) < minValue);
          } else {
            initialErrors[alertType] =
              hasEnabledChannels &&
              (!newInputValues[alertType] ||
                newInputValues[alertType].toString().trim() === '');
          }
        }
      });

      setInputErrors(initialErrors);
      setHasValidationErrors(
        Object.values(initialErrors).some((error) => error)
      );

      // Auto-expand categories that don't show display names
      if (categoriesToAutoExpand.length > 0) {
        setExpandedCategories((prev) => {
          const newExpanded = [...prev];
          categoriesToAutoExpand.forEach((categoryName) => {
            if (!newExpanded.includes(categoryName)) {
              newExpanded.push(categoryName);
            }
          });
          return newExpanded;
        });
      }
    }
  }, [alerts]);

  useEffect(() => {
    if (Object.keys(initialSwitchStates).length === 0) {
      return;
    }
    let hasAnyChanges = false;

    // Get all alert types dynamically
    const allAlertIds = Object.keys(switchStates);

    allAlertIds.forEach((alertId) => {
      const currentSwitch = switchStates[alertId] || {
        email: false,
        sms: false,
      };
      const initialSwitch = initialSwitchStates[alertId] || {
        email: false,
        sms: false,
      };

      // Check if switch states have changed
      if (
        currentSwitch.email !== initialSwitch.email ||
        currentSwitch.sms !== initialSwitch.sms
      ) {
        hasAnyChanges = true;
      }

      // Check if input values have changed for alerts that have inputs
      const alertsWithInput = [
        'PURCHASE_AMOUNT',
        'PAYMENT_DUE',
        'AVAIL_CREDIT_REMAINING',
        'BALANCE_ABOVE_THRESHOLD',
        'OUT_OF_COUNTRY',
        'OUT_OF_STATE',
      ];
      const hasEnabledChannels =
        (availableChannels[alertId]?.hasEmail && currentSwitch.email) ||
        (availableChannels[alertId]?.hasSms && currentSwitch.sms);
      if (alertsWithInput.includes(alertId) && hasEnabledChannels) {
        const currentInput = inputValues[alertId] || '';
        const initialInput = initialInputValues[alertId] || '';

        // Handle array comparisons for OUT_OF_COUNTRY and OUT_OF_STATE
        if (alertId === 'OUT_OF_COUNTRY' || alertId === 'OUT_OF_STATE') {
          const currentArray = Array.isArray(currentInput)
            ? [...currentInput]
            : [];
          const initialArray = Array.isArray(initialInput)
            ? [...initialInput]
            : [];

          if (
            JSON.stringify(currentArray.sort()) !==
            JSON.stringify(initialArray.sort())
          ) {
            hasAnyChanges = true;
          }
        } else {
          if (currentInput !== initialInput) {
            hasAnyChanges = true;
          }
        }
      }
    });

    if (hasChanges !== hasAnyChanges) {
      setHasChanges(hasAnyChanges);
    }
  }, [
    switchStates,
    inputValues,
    initialSwitchStates,
    initialInputValues,
    hasChanges,
    availableChannels,
  ]);

  useEffect(() => {
    const newInputErrors: InputErrors = {};

    // Check validation for all alert types that require inputs
    const alertsWithInput = [
      'PURCHASE_AMOUNT',
      'PAYMENT_DUE',
      'AVAIL_CREDIT_REMAINING',
      'BALANCE_ABOVE_THRESHOLD',
    ];

    alertsWithInput.forEach((alertType) => {
      const switchState = switchStates[alertType];
      if (!switchState) return;

      const hasEnabledChannels =
        (availableChannels[alertType]?.hasEmail && switchState.email) ||
        (availableChannels[alertType]?.hasSms && switchState.sms);

      if (alertType === 'PAYMENT_DUE') {
        const minValue = getAlertMinValue(alertType);
        const maxValue = getAlertMaxValue(alertType);
        newInputErrors[alertType] =
          hasEnabledChannels &&
          (!inputValues[alertType] ||
            inputValues[alertType] < minValue ||
            inputValues[alertType] > maxValue);
      } else if (alertType === 'PURCHASE_AMOUNT') {
        const minValue = getAlertMinValue(alertType);
        newInputErrors[alertType] =
          hasEnabledChannels &&
          (!inputValues[alertType] ||
            inputValues[alertType].toString().trim() === '' ||
            Number.parseFloat(inputValues[alertType]) < minValue);
      } else if (
        alertType === 'AVAIL_CREDIT_REMAINING' ||
        alertType === 'BALANCE_ABOVE_THRESHOLD'
      ) {
        const minValue = getAlertMinValue(alertType);
        newInputErrors[alertType] =
          hasEnabledChannels &&
          (!inputValues[alertType] ||
            inputValues[alertType].toString().trim() === '' ||
            Number.parseFloat(inputValues[alertType]) < minValue);
      } else {
        newInputErrors[alertType] =
          hasEnabledChannels &&
          (!inputValues[alertType] ||
            inputValues[alertType].toString().trim() === '');
      }
    });

    setInputErrors(newInputErrors);

    const hasErrors = Object.values(newInputErrors).some((error) => error);
    setHasValidationErrors(hasErrors);

    if (hasErrors && !hasChanges) {
      setHasChanges(true);
    }
  }, [switchStates, inputValues, hasChanges, availableChannels]);

  useEffect(() => {
    if (contactPrefs) {
      if (
        contactPrefs.availableCards &&
        contactPrefs.availableCards.length > 0
      ) {
        setAvailableCards(contactPrefs.availableCards);
        if (
          !selectedCard ||
          !contactPrefs.availableCards.some(
            (card: any) => card?.customerId === selectedCard?.customerId
          )
        ) {
          setSelectedCard(contactPrefs.availableCards[0]);
          previousCustomerIdRef.current =
            contactPrefs.availableCards[0]?.customerId;
        }
      }
    }
  }, [contactPrefs]);

  useEffect(() => {
    if (alertContactPrefs) {
      let hasVerifiedPhone = false;
      let hasVerifiedEmail = false;
      if (
        alertContactPrefs.programs &&
        Array.isArray(alertContactPrefs.programs)
      ) {
        alertContactPrefs.programs.forEach((program: any) => {
          if (program.selectedPhones && Array.isArray(program.selectedPhones)) {
            program.selectedPhones.forEach((phone: any) => {
              if (phone.verificationStatus === 'VERIFIED') {
                hasVerifiedPhone = true;
              }
            });
          }
          if (program.email) {
            if (program.email.verificationStatus === 'VERIFIED') {
              hasVerifiedEmail = true;
            }
          } else if (program.verificationStatus === 'VERIFIED') {
            hasVerifiedEmail = true;
          } else if (
            program.selectedEmail &&
            program.selectedEmail.verificationStatus === 'VERIFIED'
          ) {
            hasVerifiedEmail = true;
          }
        });
      }
      if (
        contactPrefs &&
        contactPrefs.emailAddress &&
        contactPrefs.emailAddress.trim() !== ''
      ) {
        hasVerifiedEmail = true;
      }
      if (
        alertContactPrefs &&
        alertContactPrefs.emailAddress &&
        alertContactPrefs.emailAddress.trim() !== ''
      ) {
        hasVerifiedEmail = true;
      }

      setSmsDisabled(!hasVerifiedPhone);

      setEmailDisabled(!hasVerifiedEmail);
    } else {
      setEmailDisabled(true);
      setSmsDisabled(true);
    }
  }, [alertContactPrefs, contactPrefs]);

  useEffect(() => {
    if (alerts && !alertsLoading) {
      setIsInitialLoading(false);
    }
  }, [alerts, alertsLoading]);

  useEffect(() => {
    return () => {
      if (error) {
        dispatch(clearErrors());
      }
    };
  }, [dispatch, error]);

  useEffect(() => {
    if (wasUpdating.current && !updateLoading && !error) {
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
      setInitialSwitchStates(JSON.parse(JSON.stringify(switchStates)));
      setInitialInputValues(JSON.parse(JSON.stringify(inputValues)));
      setHasChanges(false);
      wasUpdating.current = false;
    } else if (!updateLoading && error) {
      setShowErrorModal(true);
      wasUpdating.current = false;
    }
  }, [updateLoading, error]);

  useEffect(() => {
    if (route?.selectedCard) {
      setSelectedCard(route.selectedCard);
      previousCustomerIdRef.current = route.selectedCard.customerId;
    }
  }, [route?.selectedCard]);

  // Language detection effect
  useEffect(() => {
    const storedLanguage = localStorage.getItem('language') || 'en-US';
    setSelectedLanguage(storedLanguage);
  }, []);

  // Navigation blocking for unsaved changes
  const handleNavigationAttempt = () => {
    if (hasChanges) {
      setUnsavedChangesContext('navigation');
      setShowUnsavedChangesModal(true);
    }
  };

  const { unblockNavigation } = useNavigationBlocker({
    hasUnsavedChanges: hasChanges,
    onNavigationAttempt: handleNavigationAttempt,
    shouldBlock: true,
  });

  // Auto-update effect after disabling all alerts
  useEffect(() => {
    if (shouldAutoUpdate && Object.keys(switchStates).length > 0) {
      // Check if all alerts are actually disabled
      const allDisabled = Object.values(switchStates).every(
        (state) => !state.email && !state.sms
      );

      if (allDisabled) {
        setShouldAutoUpdate(false);
        handleUpdate();
      }
    }
  }, [shouldAutoUpdate, switchStates]);

  const toggleAlert = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((item) => item !== categoryName)
        : [...prev, categoryName]
    );
  };

  const handleSwitchChange = (alertType: string, type: string) => {
    if (type === 'sms' && smsDisabled) {
      setVerificationPopupMessage(
        rb_manage_alerts?.manageAlertsContactsVerification?.verification_popup
          ?.message
      );
      setShowVerificationPopup(true);
      setPopupType('sms');
      return;
    }

    if (type === 'email' && emailDisabled) {
      setVerificationPopupMessage(
        rb_manage_alerts?.manageAlertsContactsVerification?.email_popup?.message
      );
      setShowVerificationPopup(true);
      setPopupType('email');
      return;
    }

    if (
      alertType === 'AVAIL_CREDIT_REMAINING' &&
      type === 'email' &&
      switchStates[alertType]?.email
    ) {
      setPendingCreditAlertToggle({ alertId: alertType, type });
      setShowCreditAlertConfirmation(true);
      return;
    }

    setSwitchStates((prev) => {
      const currentState = prev[alertType] || { email: false, sms: false };
      const newState = {
        ...prev,
        [alertType]: {
          ...currentState,
          [type]: !currentState[type as 'email' | 'sms'],
        },
      };

      return newState;
    });

    // For alerts that require input validation
    const alertsWithInput = [
      'PURCHASE_AMOUNT',
      'PAYMENT_DUE',
      'AVAIL_CREDIT_REMAINING',
      'BALANCE_ABOVE_THRESHOLD',
    ];
    if (alertsWithInput.includes(alertType)) {
      const willBeEnabled =
        type === 'email'
          ? !switchStates[alertType]?.email
          : !switchStates[alertType]?.sms;
      const otherTypeEnabled =
        type === 'email'
          ? switchStates[alertType]?.sms
          : switchStates[alertType]?.email;

      // Check if any channel will be enabled and available
      const willHaveEnabledChannels =
        (type === 'email' &&
          availableChannels[alertType]?.hasEmail &&
          willBeEnabled) ||
        (type === 'sms' &&
          availableChannels[alertType]?.hasSms &&
          willBeEnabled) ||
        (type === 'email' &&
          availableChannels[alertType]?.hasSms &&
          otherTypeEnabled) ||
        (type === 'sms' &&
          availableChannels[alertType]?.hasEmail &&
          otherTypeEnabled);

      if (willHaveEnabledChannels) {
        const currentValue = inputValues[alertType] || '';
        setInputErrors((prevErrors) => ({
          ...prevErrors,
          [alertType]: !currentValue || currentValue.toString().trim() === '',
        }));
      } else {
        setInputErrors((prevErrors) => ({
          ...prevErrors,
          [alertType]: false,
        }));
      }
    }
  };

  const handleInputChange = (alertType: string, value: string) => {
    // Don't process input changes if alert is disabled (neither email nor SMS enabled) or no channels available
    const hasAvailableChannels =
      availableChannels[alertType]?.hasEmail ||
      availableChannels[alertType]?.hasSms;
    const hasEnabledChannels =
      (availableChannels[alertType]?.hasEmail &&
        switchStates[alertType]?.email) ||
      (availableChannels[alertType]?.hasSms && switchStates[alertType]?.sms);

    if (!hasAvailableChannels || !hasEnabledChannels) {
      return;
    }

    let rawValue = value;
    if (
      alertType === 'PURCHASE_AMOUNT' ||
      alertType === 'AVAIL_CREDIT_REMAINING' ||
      alertType === 'BALANCE_ABOVE_THRESHOLD'
    ) {
      // Extract numeric value from formatted input
      if (selectedLanguage === 'fr') {
        // For French: remove $ and spaces, then replace comma with period for parsing
        rawValue = rawValue.replace(/[^\d,]/g, '').replace(',', '.');
      } else {
        // For English: remove all non-numeric characters except period
        rawValue = rawValue.replace(/[^\d.]/g, '');
      }

      // Handle multiple decimal points by keeping only the first one
      const parts = rawValue.split('.');
      if (parts.length > 2) {
        rawValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    let hasError = false;
    let errorMessage = '';

    if (alertType === 'PAYMENT_DUE') {
      rawValue = rawValue.replace(/\D/g, '');

      if (rawValue.trim() === '') {
        hasError = true;
        errorMessage = rb_manage_alerts?.webAlertsHeader?.inlineErr;
      } else {
        const num = parseInt(rawValue, 10);
        const minValue = getAlertMinValue(alertType);
        const maxValue = getAlertMaxValue(alertType);
        if (isNaN(num)) {
          hasError = true;
          errorMessage = `Please enter a value between ${minValue} and ${maxValue}`;
        } else if (num < minValue) {
          hasError = true;
          // Use contentful errorMessagePaymentDueAlerts for minimum value errors
          errorMessage =
            rb_manage_alerts?.errorMessagePaymentDueAlerts ||
            `Minimum value allowed is ${minValue} day${minValue > 1 ? 's' : ''}`;
        } else if (num > maxValue) {
          hasError = true;
          // Use contentful inlineErrorMax for maximum value errors
          const contentfulMaxError = getAlertInlineErrorMax(alertType);
          errorMessage =
            contentfulMaxError ||
            `Maximum value allowed is ${maxValue} day${maxValue > 1 ? 's' : ''}`;
        }
      }
    } else if (alertType === 'AVAIL_CREDIT_REMAINING') {
      if (rawValue.trim() === '') {
        hasError = true;
        errorMessage = rb_manage_alerts?.webAlertsHeader?.inlineErr;
      } else {
        
        const num = Number.parseFloat(rawValue);
        const minValue = getAlertMinValue(alertType);
        console.log("rawValue1",rawValue,num,minValue,!Number.isNaN(num),num < minValue)
        if (!Number.isNaN(num) && num < minValue) {
          hasError = true;
          errorMessage = rb_manage_alerts?.errorMessageAvaiCredit;

        }
      }
    } else if (
      alertType === 'PURCHASE_AMOUNT' ||
      alertType === 'BALANCE_ABOVE_THRESHOLD'
    ) {
      if (rawValue.trim() === '') {
        hasError = true;
        errorMessage = rb_manage_alerts?.webAlertsHeader?.inlineErr;
      } else {
        const num = Number.parseFloat(rawValue);
        const minValue = getAlertMinValue(alertType);
        if (!Number.isNaN(num) && num < minValue) {
          hasError = true;
          errorMessage = getAlertMinInlineError(alertType) ||
            rb_manage_alerts?.errorMessageAvaiCredit;
        }
      }
    } else {
      if (rawValue.trim() === '') {
        hasError = true;
        errorMessage = rb_manage_alerts?.webAlertsHeader?.inlineErr;
      }
    }
    setInputErrors((prev) => ({
      ...prev,
      [alertType]: hasError,
    }));
    setInputErrorMessages((prev) => ({
      ...prev,
      [alertType]: errorMessage,
    }));

    setInputValues((prev) => ({
      ...prev,
      [alertType]: rawValue,
    }));
  };

  const handleFocus = (alertType: string) => {
    setIsFocused((prev) => ({
      ...prev,
      [alertType]: true,
    }));
  };

  const handleBlur = (alertType: string) => {
    // Don't process blur events if alert is disabled (neither email nor SMS enabled) or no channels available
    const hasAvailableChannels =
      availableChannels[alertType]?.hasEmail ||
      availableChannels[alertType]?.hasSms;
    const hasEnabledChannels =
      (availableChannels[alertType]?.hasEmail &&
        switchStates[alertType]?.email) ||
      (availableChannels[alertType]?.hasSms && switchStates[alertType]?.sms);

    if (!hasAvailableChannels || !hasEnabledChannels) {
      return;
    }

    setIsFocused((prev) => ({
      ...prev,
      [alertType]: false,
    }));

    const value = inputValues[alertType];

    if (value && value.trim() !== '') {
      if (
        alertType === 'PURCHASE_AMOUNT' ||
        alertType === 'AVAIL_CREDIT_REMAINING' ||
        alertType === 'BALANCE_ABOVE_THRESHOLD'
      ) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          setInputValues((prev) => ({
            ...prev,
            [alertType]: numValue.toFixed(2),
          }));
        }
      }
    } else {
      // Set default value and error state when field is empty
      if (alertType === 'PURCHASE_AMOUNT') {
        const minValue = getAlertMinValue(alertType);
        setInputValues((prev) => ({
          ...prev,
          [alertType]: '0.00',
        }));
        setInputErrorMessages((prev) => ({
          ...prev,
          [alertType]:
            getAlertMinInlineError(alertType) ||
            `Amount must be greater than $${minValue}.00`,
        }));
      } else if (
        alertType === 'AVAIL_CREDIT_REMAINING' ||
        alertType === 'BALANCE_ABOVE_THRESHOLD'
      ) {
        setInputValues((prev) => ({
          ...prev,
          [alertType]: '0.00',
        }));
      }
      setInputErrors((prev) => ({
        ...prev,
        [alertType]: true,
      }));
    }
  };

  const revertChanges = () => {
    // Revert all changes back to initial state
    setSwitchStates(JSON.parse(JSON.stringify(initialSwitchStates)));
    setInputValues(JSON.parse(JSON.stringify(initialInputValues)));
    setHasChanges(false);

    // Reset validation errors to initial state
    const initialErrors: InputErrors = {};
    Object.keys(initialSwitchStates).forEach((alertType) => {
      const switchState = initialSwitchStates[alertType];
      const alertsWithInput = [
        'PURCHASE_AMOUNT',
        'PAYMENT_DUE',
        'AVAIL_CREDIT_REMAINING',
        'BALANCE_ABOVE_THRESHOLD',
      ];

      if (alertsWithInput.includes(alertType)) {
        const hasEnabledChannels =
          (availableChannels[alertType]?.hasEmail && switchState.email) ||
          (availableChannels[alertType]?.hasSms && switchState.sms);

        if (alertType === 'PAYMENT_DUE') {
          const minValue = getAlertMinValue(alertType);
          const maxValue = getAlertMaxValue(alertType);
          initialErrors[alertType] =
            hasEnabledChannels &&
            (!initialInputValues[alertType] ||
              initialInputValues[alertType].toString().trim() === '' ||
              initialInputValues[alertType] < minValue ||
              initialInputValues[alertType] > maxValue);
        } else if (alertType === 'PURCHASE_AMOUNT') {
          const minValue = getAlertMinValue(alertType);
          initialErrors[alertType] =
            hasEnabledChannels &&
            (!initialInputValues[alertType] ||
              initialInputValues[alertType].toString().trim() === '' ||
              Number.parseFloat(initialInputValues[alertType]) < minValue);
        } else {
          initialErrors[alertType] =
            hasEnabledChannels &&
            (!initialInputValues[alertType] ||
              initialInputValues[alertType].toString().trim() === '');
        }
      }
    });

    setInputErrors(initialErrors);
    setHasValidationErrors(Object.values(initialErrors).some((error) => error));
  };

  const getRecaptchaToken = useRecaptchaToken();
  const handleUpdate = async () => {
    setIsInitialLoading(false);
    wasUpdating.current = true;
    const userNotificationPreferences: any = [];

    // Get all alert types that have changes or need to be sent
    const alertsToUpdate = new Set<string>();

    // Check which alerts have actual changes
    Object.keys(switchStates).forEach((alertType) => {
      const currentSwitch = switchStates[alertType] || {
        email: false,
        sms: false,
      };
      const initialSwitch = initialSwitchStates[alertType] || {
        email: false,
        sms: false,
      };

      // Check if switch states changed
      const switchChanged =
        currentSwitch.email !== initialSwitch.email ||
        currentSwitch.sms !== initialSwitch.sms;

      // Check if input values changed for alerts that have inputs
      const alertsWithInput = [
        'PURCHASE_AMOUNT',
        'PAYMENT_DUE',
        'AVAIL_CREDIT_REMAINING',
        'BALANCE_ABOVE_THRESHOLD',
        'OUT_OF_COUNTRY',
        'OUT_OF_STATE',
      ];
      let inputChanged = false;

      if (alertsWithInput.includes(alertType)) {
        const currentInput = inputValues[alertType] || '';
        const initialInput = initialInputValues[alertType] || '';

        if (alertType === 'OUT_OF_COUNTRY' || alertType === 'OUT_OF_STATE') {
          const currentArray = Array.isArray(currentInput)
            ? [...currentInput]
            : [];
          const initialArray = Array.isArray(initialInput)
            ? [...initialInput]
            : [];
          inputChanged =
            JSON.stringify(currentArray.sort()) !==
            JSON.stringify(initialArray.sort());
        } else {
          inputChanged = currentInput !== initialInput;
        }
      }

      // Include alert if it has changes or if it's currently enabled
      if (
        switchChanged ||
        inputChanged ||
        currentSwitch.email ||
        currentSwitch.sms
      ) {
        alertsToUpdate.add(alertType);
      }
    });

    // Convert to array for processing
    const requiredAlertIds = Array.from(alertsToUpdate);

    requiredAlertIds.forEach((alertType) => {
      const switchState = switchStates[alertType] || {
        email: false,
        sms: false,
      };
      const alertChannelInfo = availableChannels[alertType] || {
        hasEmail: false,
        hasSms: false,
      };
      let alertDetails: any = [];
      const channels: any = {};

      switch (alertType) {
        case 'PURCHASE_AMOUNT':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType]) {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: '1.0',
              },
            ];
          }
          break;
        case 'RECURRING_PURCHASE':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          alertDetails = [];
          break;
        case 'PAYMENT_DUE':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType]) {
            alertDetails = [
              {
                name: 'daysBeforeDueDate',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            alertDetails = [
              {
                name: 'daysBeforeDueDate',
                selectedValue: '5',
              },
            ];
          }
          break;
        case 'AVAIL_CREDIT_REMAINING':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType]) {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: '1.0',
              },
            ];
          }
          break;
        case 'BALANCE_ABOVE_THRESHOLD':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType]) {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            alertDetails = [
              {
                name: 'thresholdAmount',
                selectedValue: '1.0',
              },
            ];
          }
          break;
        case 'OUT_OF_COUNTRY':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType] && Array.isArray(inputValues[alertType])) {
            alertDetails = [
              {
                name: 'listOfCountries',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            // Find the current alert details from the API response to get the current selectedValue
            const currentAlert = alerts
              .find((category: AlertCategory) =>
                category.details?.find(
                  (alert: Alert) => alert.alert === alertType
                )
              )
              ?.details?.find((alert: Alert) => alert.alert === alertType);

            const currentSelectedValue = currentAlert?.alertDetails?.find(
              (detail: AlertDetail) => detail.name === 'listOfCountries'
            )?.selectedValue;

            alertDetails = [
              {
                name: 'listOfCountries',
                selectedValue: Array.isArray(currentSelectedValue)
                  ? currentSelectedValue
                  : [currentSelectedValue].filter(Boolean),
              },
            ];
          }
          break;
        case 'OUT_OF_STATE':
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          if (inputValues[alertType] && Array.isArray(inputValues[alertType])) {
            alertDetails = [
              {
                name: 'listOfStatesProvinces',
                selectedValue: inputValues[alertType],
              },
            ];
          } else {
            // Find the current alert details from the API response to get the current selectedValue
            const currentAlert = alerts
              .find((category: AlertCategory) =>
                category.details?.find(
                  (alert: Alert) => alert.alert === alertType
                )
              )
              ?.details?.find((alert: Alert) => alert.alert === alertType);

            const currentSelectedValue = currentAlert?.alertDetails?.find(
              (detail: AlertDetail) => detail.name === 'listOfStatesProvinces'
            )?.selectedValue;

            alertDetails = [
              {
                name: 'listOfStatesProvinces',
                selectedValue: Array.isArray(currentSelectedValue)
                  ? currentSelectedValue
                  : [currentSelectedValue].filter(Boolean),
              },
            ];
          }
          break;
        default:
          // Handle all other alert types with basic channel setup
          if (alertChannelInfo.hasEmail) {
            channels.EMAIL = switchState.email ? 'true' : 'false';
          }
          if (alertChannelInfo.hasSms) {
            channels.SMS = switchState.sms ? 'true' : 'false';
          }
          alertDetails = [];
          break;
      }

      const isEnabled = switchState.email || switchState.sms;

      userNotificationPreferences.push({
        alert: alertType,
        channels: channels,
        alertDetails,
        activeFlag: isEnabled,
      });
    });

    if (accountDetails) {
      const accountId = accountDetails.accountId;
      const customerId = selectedCard
        ? selectedCard.customerId
        : accountDetails.customer?.customerId;

      if (accountId && customerId) {
        const token = await getRecaptchaToken(
          'manage_alert_prefrence_type_update'
        );
        console.log('tokenalert', token);
        dispatch(
          updateAlertPreferencesRequest({
            accountId,
            customerId,
            userNotificationPreferences,
            recaptchaToken: token,
          })
        );
      }
    }
  };

  const openAlertPreferencesModal = () => {
    setIsAlertsModalOpen(true);
  };

  const closeModal = () => {
    setIsAlertsModalOpen(false);
  };

  const handleDisableAllAlerts = () => {
    setShowDisableAllConfirmation(true);
  };

  const confirmDisableAllAlerts = () => {
    const newSwitchStates: SwitchStates = {};

    // Disable all alerts (set both email and SMS to false)
    Object.keys(switchStates).forEach((alertType) => {
      newSwitchStates[alertType] = {
        email: false,
        sms: false,
      };
    });

    setSwitchStates(newSwitchStates);
    setShowDisableAllConfirmation(false);
    setShouldAutoUpdate(true);
  };

  const cancelDisableAllAlerts = () => {
    setShowDisableAllConfirmation(false);
  };

  const breadcrumbs = [
    { name: rb_navigation?.moreLogoTitle, href: '/' },
    {
      name: rb_manage_alerts?.mangaeAlertsNavigationHeaderTitlte,
      href: '/manage-alerts',
    },
  ];

  return (
    <div className="bg-bg-body py-4 md:px-8 px-3">
      <Breadcrumbs items={breadcrumbs} />
      <div className="w-full m-[22px] mx-auto rounded-[15px] bg-white min-h-[70vh] my-0">
        <div className="bg-white md:p-6 p-3 rounded-lg">
          <h2 className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] pb-7">
            {rb_manage_alerts?.mangaeAlertsNavigationHeaderTitlte}
          </h2>

          {/* Alerts Changes Banner */}
          {rb_manage_alerts?.webAlertsHeader?.showAlertsBanner && (
            <div className="flex items-center gap-4 px-6 py-4 mb-6 border-l-[6px] border-blue-500 bg-blue-50">
              <div className="flex-1">
                <h3 className="text-[16px] font-semibold text-[#1f1f1f] mb-2">
                  {rb_manage_alerts?.webAlertsHeader?.bannerText?.title ||
                    'Alerts changes'}
                </h3>
                <div className="text-[14px] text-[#1f1f1f] leading-5">
                  {(() => {
                    const bannerText =
                      rb_manage_alerts?.webAlertsHeader?.bannerText;
                    const linkData = bannerText?.linkData;
                    const description =
                      bannerText?.description ||
                      'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';

                    // Helper function to render text with line breaks
                    const renderTextWithLineBreaks = (text: string) => {
                      return text
                        .split('\n')
                        .map((line: string, index: number, array: string[]) => (
                          <span key={index}>
                            {line}
                            {index < array.length - 1 && <br />}
                          </span>
                        ));
                    };

                    // Handle %s replacement based on linkRequired flag
                    if (linkData) {
                      if (
                        linkData.linkRequired &&
                        linkData.linkText &&
                        linkData.redirectionLink
                      ) {
                        // Replace %s with a clickable link
                        if (description.includes('%s')) {
                          const parts = description.split('%s');
                          return (
                            <>
                              {renderTextWithLineBreaks(parts[0])}
                              <a
                                href={linkData.redirectionLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline hover:text-blue-800"
                              >
                                {linkData.linkText}
                              </a>
                              {renderTextWithLineBreaks(parts[1])}
                            </>
                          );
                        } else {
                          // No %s found, just return description with line breaks
                          return renderTextWithLineBreaks(description);
                        }
                      } else {
                        // linkRequired is false, replace %s with empty string
                        const cleanedDescription = description.replace(
                          '%s',
                          ''
                        );
                        return renderTextWithLineBreaks(cleanedDescription);
                      }
                    } else {
                      // No linkData, return description with line breaks (this maintains backward compatibility)
                      return renderTextWithLineBreaks(description);
                    }
                  })()}
                </div>
              </div>
            </div>
          )}

          <div className="flex lg:flex-row flex-col lg:gap-4">
            <div className="lg:w-1/3 w-full">
              {rb_manage_alerts?.mangaeAlertsInformationText &&
                (() => {
                  const [firstPart, secondPart] =
                    rb_manage_alerts.mangaeAlertsInformationText.split(
                      'Please note that'
                    );
                  return (
                    <>
                      <p className="md:text-[22px] text-[16px] text-[#1f1f1f] mb-5 tracking-[0.077px] leading-7">
                        {firstPart.trim()}
                      </p>
                      {/* <p className="text-[16px] text-[#1f1f1f] mb-8 tracking-[-0.051px] leading-5">
                          Please note that {secondPart?.trim()}
                        </p> */}
                    </>
                  );
                })()}
              <div className="flex flex-col md:flex-row lg:flex-col sm:gap-0 lg:gap-0 md:gap-4 lg:mb-0 mb-4">
                {availableCards?.length > 1 && (
                  <div className="flex flex-col w-full">
                    <p className="text-[15px] text-[#1f1f1f] font-medium">
                      {rb_manage_alerts?.webAlertsHeader?.showing}
                    </p>
                    <CardDropdown
                      availableCards={availableCards}
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                    />
                  </div>
                )}

                <button
                  onClick={openAlertPreferencesModal}
                  className={`flex items-center h-20 gap-3 border border-[#E9E9E9] bg-[#FAFAFA] px-4 py-2 rounded-lg hover:bg-red-50 transition lg:mb-0 ${availableCards?.length <= 1 ? 'md:w-1/2 lg:w-[90%] w-full' : 'lg:w-[90%] w-full'}`}
                >
                  <img
                    src={assets?.alert_pref_icon}
                    alt="alertPrefIcon"
                    className="w-10 h-10 hidden md:block"
                  />
                  <p className="text-[#1f1f1f] text-[17px] tracking-[-0.07px] leading-5">
                    {rb_manage_alerts?.manageAlertsAlertPrefrences}
                  </p>
                  <img
                    src={assets?.right_arrow}
                    alt="rightArrow"
                    className="w-6 h-6"
                  />
                </button>
              </div>
            </div>
            {alertsLoading ? (
              <div className="flex justify-center items-center h-[30vh] w-full lg:w-2/3 mx-auto">
                <Spinner aria-label="Loading" />
              </div>
            ) : (
              <div className="lg:w-2/3 w-full space-y-6">
                {/* Single Container for All Alerts */}
                <div className="border border-gray-300 rounded-xl overflow-hidden">
                  {/* Account Alerts Categories Header - First element in container */}
                  <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                    <h2 className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] text-[#1f1f1f]">
                      {
                        rb_manage_alerts?.webAlertsHeader
                          ?.accountAlertsCategories
                      }
                    </h2>
                  </div>

                  {alerts.map(
                    (category: AlertCategory, categoryIndex: number) => {
                      // Get category key for visibility checks
                      const categoryKey = getCategoryKeyFromName(
                        category.categoryName
                      );

                      // Check if entire category should be hidden
                      if (shouldHideEntireCategoryOnWeb(categoryKey)) {
                        return null;
                      }

                      // Filter alerts in this category to only show those in the mapping and web-visible
                      const filteredAlerts =
                        category.details?.filter(
                          (alert: Alert) =>
                            isAlertInMapping(alert.alert) &&
                            shouldShowAlertOnWeb(alert.alert)
                        ) || [];

                      // Only show category if it has alerts that are in the mapping and web-visible
                      if (filteredAlerts.length === 0) {
                        return null;
                      }

                      // Calculate active alert count for filtered alerts
                      const activeAlertCount = filteredAlerts.filter(
                        (alert: Alert) => alert.activeFlag === 'true'
                      ).length;
                      // Check if category display name should be shown
                      const showCategoryDisplayName =
                        shouldShowCategoryDisplayNameOnWeb(categoryKey);
                      return (
                        <div key={category.categoryName}>
                          {/* Category Header - only shown if showCategoryDisplayName is true */}
                          {showCategoryDisplayName && (
                            <div
                              className="bg-white border-t border-gray-200 px-6 py-4 cursor-pointer"
                              onClick={() =>
                                toggleCategory(category.categoryName)
                              }
                            >
                              <div className="flex items-start gap-2">
                                <img
                                  src={
                                    expandedCategories.includes(
                                      category.categoryName
                                    )
                                      ? assets?.minus_icon
                                      : assets?.plus_icon
                                  }
                                  alt={
                                    expandedCategories.includes(
                                      category.categoryName
                                    )
                                      ? 'Collapse category'
                                      : 'Expand category'
                                  }
                                  className="w-8 h-8 mt-0.5"
                                />
                                <div className="flex-1">
                                  <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                                    <h3 className="text-[17px] font-medium text-[#1f1f1f] tracking-[-0.07px] leading-5">
                                      {getCategoryDisplayName(categoryKey)}
                                    </h3>
                                    <span className="text-sm text-gray-600 mt-1 md:mt-0 md:ml-2">
                                      {rb_manage_alerts?.webAlertsHeader
                                        ?.fetchAlerts
                                        ? rb_manage_alerts.webAlertsHeader.fetchAlerts.replace(
                                            '%s',
                                            activeAlertCount.toString()
                                          )
                                        : `You have ${activeAlertCount} active alerts`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Alert Type Header - shown when category is expanded OR when category display name is hidden */}
                          {(expandedCategories.includes(
                            category.categoryName
                          ) ||
                            !showCategoryDisplayName) && (
                            <>
                              <div className="flex w-full h-8 px-6 py-4 justify-between items-center flex-shrink-0 rounded-t-[10px] border-b border-[#E9E9E9] bg-[#FAFAFA]">
                                <p className="text-[#1f1f1f] text-[17px] tracking-[-0.07px] leading-5 font-medium">
                                  {
                                    rb_manage_alerts?.manageAlertsHeader
                                      ?.alerttype
                                  }
                                </p>
                                <div className="flex items-center">
                                  {/* Check if any alert in this category has email available */}
                                  {filteredAlerts.some(
                                    (alert: Alert) =>
                                      availableChannels[alert.alert]?.hasEmail
                                  ) && (
                                    <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 w-16 text-center">
                                      {
                                        rb_manage_alerts?.manageAlertsHeader
                                          ?.email
                                      }
                                    </p>
                                  )}
                                  {/* Check if any alert in this category has SMS available */}
                                  {filteredAlerts.some(
                                    (alert: Alert) =>
                                      availableChannels[alert.alert]?.hasSms
                                  ) && (
                                    <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 w-16 text-center ml-16 md:ml-20">
                                      {
                                        rb_manage_alerts?.manageAlertsHeader
                                          ?.sms
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Category Details */}
                              <div className="space-y-0">
                                {filteredAlerts.map(
                                  (alert: Alert, alertIndex: number) => {
                                    // Individual alert expansion (for showing input fields) - separate from category expansion
                                    const isExpanded = expandedIds.includes(
                                      alert.alert
                                    );
                                    const isLast =
                                      alertIndex === filteredAlerts.length - 1;
                                    const hasAlertDetails =
                                      alert.alertDetails &&
                                      alert.alertDetails.length > 0;

                                    return (
                                      <div
                                        key={alert.alert}
                                        className={`${!isLast ? 'border-b border-gray-200' : ''}`}
                                      >
                                        <div className="flex items-center md:mx-6 mx-2 my-4 justify-between">
                                          <div className="flex items-center gap-1 flex-1">
                                            {hasAlertDetails ? (
                                              <img
                                                src={
                                                  isExpanded
                                                    ? assets?.minus_icon
                                                    : assets?.plus_icon
                                                }
                                                alt={
                                                  isExpanded
                                                    ? 'Collapse section'
                                                    : 'Expand section'
                                                }
                                                role="button"
                                                aria-expanded={isExpanded}
                                                aria-label={
                                                  isExpanded
                                                    ? 'Collapse section'
                                                    : 'Expand section'
                                                }
                                                className="w-8 h-8 cursor-pointer"
                                                onClick={() =>
                                                  toggleAlert(alert.alert)
                                                }
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                  if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                  ) {
                                                    toggleAlert(alert.alert);
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <div className="w-8 h-8" />
                                            )}
                                            <span className="md:text-[17px] text-[16px] text-[#1f1f1f] tracking-[-0.07px] leading-5 md:ml-4 ml-1">
                                              {getAlertDisplayName(alert.alert)}
                                            </span>
                                          </div>
                                          <div className="flex items-center">
                                            {/* Check if category has any email alerts to determine if email column exists */}
                                            {filteredAlerts.some(
                                              (catAlert: Alert) =>
                                                availableChannels[
                                                  catAlert.alert
                                                ]?.hasEmail
                                            ) && (
                                              <div className="flex items-center justify-center w-16">
                                                {/* Email Switch - only show if available for this specific alert */}
                                                {availableChannels[alert.alert]
                                                  ?.hasEmail ? (
                                                  <AlertNotificationSwitch
                                                    alertId={alert.alert}
                                                    type="email"
                                                    checked={
                                                      switchStates[alert.alert]
                                                        ?.email || false
                                                    }
                                                    onChange={
                                                      handleSwitchChange
                                                    }
                                                    disabled={emailDisabled}
                                                  />
                                                ) : (
                                                  <div></div>
                                                )}
                                              </div>
                                            )}

                                            {/* Check if category has any SMS alerts to determine if SMS column exists */}
                                            {filteredAlerts.some(
                                              (catAlert: Alert) =>
                                                availableChannels[
                                                  catAlert.alert
                                                ]?.hasSms
                                            ) && (
                                              <div className="flex items-center justify-center w-16 ml-16 md:ml-20">
                                                {/* SMS Switch - only show if available for this specific alert */}
                                                {availableChannels[alert.alert]
                                                  ?.hasSms ? (
                                                  <AlertNotificationSwitch
                                                    alertId={alert.alert}
                                                    type="sms"
                                                    checked={
                                                      switchStates[alert.alert]
                                                        ?.sms || false
                                                    }
                                                    onChange={
                                                      handleSwitchChange
                                                    }
                                                    disabled={smsDisabled}
                                                  />
                                                ) : (
                                                  <div></div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {isExpanded && hasAlertDetails && (
                                          <div className="ml-[72px] my-4 mr-6">
                                            {/* Render different input types based on alert type */}
                                            {[
                                              'PURCHASE_AMOUNT',
                                              'AVAIL_CREDIT_REMAINING',
                                              'BALANCE_ABOVE_THRESHOLD',
                                            ].includes(alert.alert) && (
                                              <div
                                                className={`${(!availableChannels[alert.alert]?.hasEmail || !switchStates[alert.alert]?.email) && (!availableChannels[alert.alert]?.hasSms || !switchStates[alert.alert]?.sms) ? 'opacity-50' : ''}`}
                                              >
                                                <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 mb-2">
                                                  {getAlertInputLabel(
                                                    alert.alert
                                                  ) ||
                                                    'Alert me when the amount is equal to or greater than'}
                                                </p>
                                                <input
                                                  data-testid={`input-${alert.alert}`}
                                                  value={
                                                    inputValues[alert.alert] !==
                                                      undefined &&
                                                    inputValues[alert.alert] !==
                                                      null &&
                                                    inputValues[alert.alert]
                                                      .toString()
                                                      .trim() !== ''
                                                      ? isFocused[alert.alert]
                                                        ? inputValues[
                                                            alert.alert
                                                          ]
                                                        : formatCurrencyForDisplay(
                                                            inputValues[
                                                              alert.alert
                                                            ]
                                                          )
                                                      : ''
                                                  }
                                                  placeholder={getCurrencyPlaceholder()}
                                                  onChange={(e) =>
                                                    handleInputChange(
                                                      alert.alert,
                                                      e.target.value
                                                    )
                                                  }
                                                  onFocus={() =>
                                                    handleFocus(alert.alert)
                                                  }
                                                  onBlur={() =>
                                                    handleBlur(alert.alert)
                                                  }
                                                  disabled={
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasEmail ||
                                                      !switchStates[alert.alert]
                                                        ?.email) &&
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasSms ||
                                                      !switchStates[alert.alert]
                                                        ?.sms)
                                                  }
                                                  className={`w-full border px-3 py-2 text-sm mt-2 rounded ${
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasEmail ||
                                                      !switchStates[alert.alert]
                                                        ?.email) &&
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasSms ||
                                                      !switchStates[alert.alert]
                                                        ?.sms)
                                                      ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                                      : inputErrors[alert.alert]
                                                        ? 'border-red-500 bg-white'
                                                        : 'border-gray-300 bg-white'
                                                  }`}
                                                />
                                                {inputErrors[alert.alert] && (
                                                  <>
                                                   
                                                    <div className="flex items-center mt-1">
                                                      <img
                                                        src={assets?.error_icon}
                                                        alt={`errorIcon`}
                                                        className="w-6 h-6"
                                                      />
                                                      <p className="text-red-600 text-xs ml-1">
                                                        {inputErrorMessages[
                                                          alert.alert
                                                        ] ||
                                                          ([
                                                            'PURCHASE_AMOUNT',
                                                            'AVAIL_CREDIT_REMAINING',
                                                            'BALANCE_ABOVE_THRESHOLD',
                                                          ].includes(alert.alert)
                                                            ? getAlertMinInlineError(
                                                                alert.alert
                                                              ) ||
                                                              rb_manage_alerts?.errorMessageAvaiCredit
                                                            : 'This field is required')}
                                                      </p>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            )}

                                            {alert.alert === 'PAYMENT_DUE' && (
                                              <div
                                                className={`${(!availableChannels[alert.alert]?.hasEmail || !switchStates[alert.alert]?.email) && (!availableChannels[alert.alert]?.hasSms || !switchStates[alert.alert]?.sms) ? 'opacity-50' : ''}`}
                                              >
                                                <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 mb-2">
                                                  {getAlertInputLabel(
                                                    alert.alert
                                                  ) ||
                                                    'Alert me this many days before my payment due date'}
                                                </p>
                                                <input
                                                  data-testid={`input-${alert.alert}`}
                                                  value={
                                                    inputValues[alert.alert] !==
                                                      undefined &&
                                                    inputValues[alert.alert] !==
                                                      null &&
                                                    inputValues[alert.alert]
                                                      .toString()
                                                      .trim() !== ''
                                                      ? inputValues[alert.alert]
                                                      : ''
                                                  }
                                                  onChange={(e) =>
                                                    handleInputChange(
                                                      alert.alert,
                                                      e.target.value
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    handleBlur(alert.alert)
                                                  }
                                                  disabled={
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasEmail ||
                                                      !switchStates[alert.alert]
                                                        ?.email) &&
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasSms ||
                                                      !switchStates[alert.alert]
                                                        ?.sms)
                                                  }
                                                  className={`w-full border px-3 py-2 text-sm mt-2 rounded ${
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasEmail ||
                                                      !switchStates[alert.alert]
                                                        ?.email) &&
                                                    (!availableChannels[
                                                      alert.alert
                                                    ]?.hasSms ||
                                                      !switchStates[alert.alert]
                                                        ?.sms)
                                                      ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                                      : inputErrors[alert.alert]
                                                        ? 'border-red-500 bg-white'
                                                        : 'border-gray-300 bg-white'
                                                  }`}
                                                />
                                                {inputErrors[alert.alert] && (
                                                  <div className="flex items-center mt-1">
                                                    <img
                                                      src={assets?.error_icon}
                                                      alt="errorIcon"
                                                      className="w-6 h-6"
                                                    />
                                                    <p className="text-red-600 text-xs ml-1">
                                                      {inputErrorMessages[
                                                        alert.alert
                                                      ] ||
                                                        `Please enter a value between ${getAlertMinValue(alert.alert)} and ${getAlertMaxValue(alert.alert)}`}
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {alert.alert ===
                                              'OUT_OF_COUNTRY' && (
                                              <div
                                                className={`${(!availableChannels[alert.alert]?.hasEmail || !switchStates[alert.alert]?.email) && (!availableChannels[alert.alert]?.hasSms || !switchStates[alert.alert]?.sms) ? 'opacity-50' : ''}`}
                                              >
                                                <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 mb-2">
                                                  {getAlertInputLabel(
                                                    alert.alert
                                                  ) || 'List of countries'}
                                                </p>
                                                {alert.alertDetails.find(
                                                  (detail) =>
                                                    detail.name ===
                                                    'listOfCountries'
                                                ) && (
                                                  <MultiSelectDropdown
                                                    options={getCountryOptionsForDropdown(
                                                      alert.alertDetails.find(
                                                        (detail) =>
                                                          detail.name ===
                                                          'listOfCountries'
                                                      )?.availableValues || []
                                                    )}
                                                    selectedValues={getSelectedCountryDisplayNames(
                                                      inputValues[
                                                        alert.alert
                                                      ] || []
                                                    )}
                                                    onChange={(
                                                      displayNames
                                                    ) => {
                                                      const codes =
                                                        getCountryCodesFromDisplayNames(
                                                          displayNames
                                                        );
                                                      setInputValues(
                                                        (prev) => ({
                                                          ...prev,
                                                          [alert.alert]: codes,
                                                        })
                                                      );
                                                    }}
                                                    placeholder="Select countries..."
                                                    searchPlaceholder="Search countries..."
                                                    disabled={
                                                      (!availableChannels[
                                                        alert.alert
                                                      ]?.hasEmail ||
                                                        !switchStates[
                                                          alert.alert
                                                        ]?.email) &&
                                                      (!availableChannels[
                                                        alert.alert
                                                      ]?.hasSms ||
                                                        !switchStates[
                                                          alert.alert
                                                        ]?.sms)
                                                    }
                                                  />
                                                )}
                                              </div>
                                            )}

                                            {alert.alert === 'OUT_OF_STATE' && (
                                              <div
                                                className={`${(!availableChannels[alert.alert]?.hasEmail || !switchStates[alert.alert]?.email) && (!availableChannels[alert.alert]?.hasSms || !switchStates[alert.alert]?.sms) ? 'opacity-50' : ''}`}
                                              >
                                                <p className="text-[#1f1f1f] text-[16px] tracking-[-0.051px] leading-5 mb-2">
                                                  {getAlertInputLabel(
                                                    alert.alert
                                                  ) ||
                                                    'List of states/provinces'}
                                                </p>
                                                {alert.alertDetails.find(
                                                  (detail) =>
                                                    detail.name ===
                                                    'listOfStatesProvinces'
                                                ) && (
                                                  <MultiSelectDropdown
                                                    options={getStateProvinceOptionsForDropdown(
                                                      alert.alertDetails.find(
                                                        (detail) =>
                                                          detail.name ===
                                                          'listOfStatesProvinces'
                                                      )?.availableValues || []
                                                    )}
                                                    selectedValues={getSelectedStateProvinceDisplayNames(
                                                      inputValues[
                                                        alert.alert
                                                      ] || []
                                                    )}
                                                    onChange={(
                                                      displayNames
                                                    ) => {
                                                      const codes =
                                                        getStateProvinceCodesFromDisplayNames(
                                                          displayNames
                                                        );
                                                      setInputValues(
                                                        (prev) => ({
                                                          ...prev,
                                                          [alert.alert]: codes,
                                                        })
                                                      );
                                                    }}
                                                    placeholder="Select states/provinces..."
                                                    searchPlaceholder="Search states/provinces..."
                                                    disabled={
                                                      (!availableChannels[
                                                        alert.alert
                                                      ]?.hasEmail ||
                                                        !switchStates[
                                                          alert.alert
                                                        ]?.email) &&
                                                      (!availableChannels[
                                                        alert.alert
                                                      ]?.hasSms ||
                                                        !switchStates[
                                                          alert.alert
                                                        ]?.sms)
                                                    }
                                                  />
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>

                {/* Disable All Alerts Button */}
                <div className="mb-6">
                  <button
                    onClick={handleDisableAllAlerts}
                    className="w-full px-6 py-3 border border-[#DA291C] text-[#DA291C] bg-white text-lg"
                  >
                    {rb_manage_alerts?.webAlertsHeader?.disableAllAlertsCta ||
                      'Disable all alerts'}
                  </button>
                </div>

                <div className="flex justify-center gap-5 h-12">
                  <button
                    onClick={() => {
                      if (hasChanges) {
                        setUnsavedChangesContext('form');
                        setShowUnsavedChangesModal(true);
                      }
                    }}
                    disabled={!hasChanges}
                    className={`px-6 py-2 ${hasChanges ? 'border-[#DA291C] text-[#DA291C] bg-[#fff] hover:bg-red-50' : 'border-[#D9D9D9] bg-[#F5F5F5] text-gray-700'} border text-lg w-36 rounded`}
                  >
                    {rb_manage_alerts?.webAlertsHeader?.cancelCta || 'Cancel'}
                  </button>
                  <button
                    onClick={() => {
                      if (hasValidationErrors) return;
                      handleUpdate();
                    }}
                    disabled={
                      !hasChanges || hasValidationErrors || updateLoading
                    }
                    className={`px-6 py-2 ${
                      !hasChanges || hasValidationErrors || updateLoading
                        ? 'bg-[#CCCCCC] text-gray-700'
                        : 'bg-[#DA291C] text-white hover:bg-red-700'
                    } text-lg w-36 rounded`}
                  >
                    {updateLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      </div>
                    ) : (
                      rb_manage_alerts?.webAlertsHeader?.updateCta || 'Update'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <ManageAlertsModals
          rb_manage_alerts={rb_manage_alerts}
          assets={assets}
          showSuccessModal={showSuccessModal}
          setShowSuccessModal={setShowSuccessModal}
          showVerificationPopup={showVerificationPopup}
          setShowVerificationPopup={setShowVerificationPopup}
          showCreditAlertConfirmation={showCreditAlertConfirmation}
          setShowCreditAlertConfirmation={setShowCreditAlertConfirmation}
          showUnsavedChangesModal={showUnsavedChangesModal}
          setShowUnsavedChangesModal={setShowUnsavedChangesModal}
          unsavedChangesContext={unsavedChangesContext}
          showErrorModal={showErrorModal}
          setShowErrorModal={setShowErrorModal}
          popupType={popupType}
          verificationPopupMessage={verificationPopupMessage}
          pendingCreditAlertToggle={pendingCreditAlertToggle}
          setPendingCreditAlertToggle={setPendingCreditAlertToggle}
          setSwitchStates={setSwitchStates}
          setInputErrors={setInputErrors}
          revertChanges={revertChanges}
          unblockNavigation={unblockNavigation}
        />

        {/* Disable All Alerts Confirmation Modal */}
        <Modal
          isOpen={showDisableAllConfirmation}
          onRequestClose={cancelDisableAllAlerts}
          contentLabel="Disable All Alerts Confirmation"
          className="fixed inset-0 flex items-center justify-center p-4 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-center text-[#1f1f1f]">
              {rb_manage_alerts?.webAlertsHeader?.confirmationDialog?.title ||
                'Are you sure?'}
            </h2>
            <p className="text-center text-[#1f1f1f] mb-6">
              {rb_manage_alerts?.webAlertsHeader?.confirmationDialog?.message ||
                'Pressing OK will disable all your alerts.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={confirmDisableAllAlerts}
                className="w-full px-6 py-3 bg-[#DA291C] text-white text-lg rounded hover:bg-red-700 transition-colors duration-200"
              >
                {rb_manage_alerts?.webAlertsHeader?.confirmationDialog
                  ?.primaryCta || 'OK'}
              </button>
              <button
                onClick={cancelDisableAllAlerts}
                className="w-full px-6 py-3 border border-[#DA291C] text-[#DA291C] bg-white text-lg rounded hover:bg-red-50 transition-colors duration-200"
              >
                {rb_manage_alerts?.webAlertsHeader?.confirmationDialog
                  ?.secondaryCta || 'Cancel'}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isAlertsModalOpen}
          contentLabel="Alert Preferences Modal"
          className="fixed right-0 top-0 bottom-0 p-0 md:m-2 xl:m-2 w-full lg:w-[35%] md:w-[60%] md:h-auto h-[100%] outline-none flex flex-col"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          {/* <GoogleReCaptchaProvider
            reCaptchaKey={environment?.recaptchaSiteKey}
            container={{
              parameters: {
                badge: 'bottomright',
                theme: 'light',
              },
            }}
            scriptProps={{ async: true, defer: true }}
          > */}
          <ChangeAlertPreferences
            rb_manage_alerts={rb_manage_alerts}
            assets={assets}
            onClose={closeModal}
            contactPrefs={contactPrefs}
            alertContactPrefs={alertContactPrefs}
            route={{
              params: {
                selectedCard,
                availableCards,
                isAlertsModalOpen,
                setIsAlertsModalOpen,
                setSelectedCard,
              },
            }}
          />
          {/* </GoogleReCaptchaProvider> */}
        </Modal>
      </div>
    </div>
  );
};

export default ManageAlerts;
